import { useRouter } from '@tanstack/react-router'
import {
  type AnchorHTMLAttributes,
  forwardRef,
  type MouseEvent,
  useCallback,
} from 'react'

// Drop-in replacement for `next/link` backed by TanStack Router. Renders a
// plain anchor and intercepts left-clicks for client-side navigation, so it
// accepts any string href (paths, query strings, hashes, external URLs)
// without the typed-route constraints of TanStack's own <Link>.
type NextLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string
  replace?: boolean
  // Accepted for API compatibility with next/link; intentionally unused.
  prefetch?: boolean
  scroll?: boolean
  shallow?: boolean
}

function isModifiedEvent(e: MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
}

function isExternal(href: string): boolean {
  return (
    /^[a-z][a-z0-9+.-]*:/i.test(href) && !href.startsWith('/') // any scheme
  )
}

// Schemes that must not be navigated to via the rendered <a href>. CSP blocks
// most of these already, but the shim should refuse to render them as live
// links if any markdown/user-supplied URL ever reaches it.
const UNSAFE_HREF = /^\s*(javascript|data|vbscript):/i

function sanitizeHref(href: string): string {
  return UNSAFE_HREF.test(href) ? '#' : href
}

const Link = forwardRef<HTMLAnchorElement, NextLinkProps>(function Link(
  {
    href,
    replace,
    prefetch: _p,
    scroll: _s,
    shallow: _sh,
    onClick,
    target,
    ...rest
  },
  ref,
) {
  const router = useRouter()
  const safeHref = sanitizeHref(href)

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e)
      // Unsafe scheme — neutralized to '#'; suppress any navigation entirely.
      if (UNSAFE_HREF.test(href)) {
        e.preventDefault()
        return
      }
      // Hash-only and external links use native anchor behaviour.
      if (href.startsWith('#') || isExternal(href)) return
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        isModifiedEvent(e) ||
        (target && target !== '_self')
      ) {
        return
      }
      e.preventDefault()
      if (replace) router.history.replace(href)
      else router.history.push(href)
    },
    [href, replace, target, onClick, router],
  )

  return (
    <a
      ref={ref}
      href={safeHref}
      onClick={handleClick}
      target={target}
      {...rest}
    />
  )
})

export default Link
